import { Download, FileText } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import KeyValueListDisplay from '@/components/KeyValueDisplay';
import Loader from '@/components/Loader';
import {
  convertISODateToMMDDYYYY,
  convertISOToTime,
  downloadFilefromBuffer,
} from '@/lib/utils';
import { BillingAmount } from '@/components/BillingAmount';
import { displayUserFacingError } from '@/lib/error';
import { downloadDdiInvoicePdf } from '@/services/dueDiligenceService';
import { EmailLogStatusLabel, invoiceStatusBadgeDetails } from '@/utils/constant';
import type { DdiInvoiceEmailLog, DdiInvoiceResponse } from '@/types/dueDiligence.types';
import { EmailStatus } from '@/types/types';
import { InvoiceStatusBadge } from '@pages/caseManagement/invoice/InvoiceView';
import { toast } from '@/components/ui/use-toast';

type Props = {
  invoice: DdiInvoiceResponse | null | undefined;
  workOrderId: string;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** When false, load failures keep the Retry UI but do not toast (e.g. Billing tab not active). */
  notifyLoadErrors?: boolean;
};

const getEmailLogTimestamp = (sentAt: string | null): number => {
  if (!sentAt) return Number.NEGATIVE_INFINITY;
  const parsed = Date.parse(sentAt);
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
};

const getLastFiveEmailLogs = (emailLogs: DdiInvoiceEmailLog[]): DdiInvoiceEmailLog[] =>
  [...emailLogs]
    .sort((a, b) => getEmailLogTimestamp(b.sentAt) - getEmailLogTimestamp(a.sentAt))
    .slice(0, 5);

export const BillingDdiContent = ({
  invoice,
  workOrderId,
  isLoading,
  isError,
  onRetry,
  notifyLoadErrors = true,
}: Props) => {
  const navigate = useNavigate();
  const status = invoice?.status;
  const totalAmount = invoice?.totalAmount ?? 0;
  const previousErrorRef = useRef(false);

  const emailLogs = useMemo(() => getLastFiveEmailLogs(invoice?.emailLogs ?? []), [invoice?.emailLogs]);

  useEffect(() => {
    if (!notifyLoadErrors) {
      // Allow a toast when the Billing tab becomes active while still in an error state.
      previousErrorRef.current = false;
      return;
    }
    if (isError && !previousErrorRef.current) {
      const message = invoice
        ? 'Failed to load invoice email log. Please try again.'
        : 'Failed to load billing details. Please try again.';
      displayUserFacingError(message);
    }
    previousErrorRef.current = isError;
  }, [isError, invoice, notifyLoadErrors]);

  const handleDownload = async (invoiceId: string, invoiceNumber: string) => {
    toast({
      description: 'Download will start shortly',
      duration: 3000,
    });
    const result = await downloadDdiInvoicePdf(invoiceId);
    if (result) {
      downloadFilefromBuffer(result.blob, result.fileName ?? `${invoiceNumber}.pdf`, 'PDF');
    } else {
      toast({
        description:
          'Unable to generate PDF. If this keeps happening, restart the API server and try again.',
        duration: 5000,
        variant: 'destructive',
      });
    }
  };

  const goToInvoice = () => {
    if (!invoice) return;
    navigate(`/due-diligence/invoice/${invoice.invoiceNumber}?workOrderId=${workOrderId}`);
  };

  // Keep previously loaded invoice/logs visible on refresh failure; only block when there is no data.
  if (isError && !invoice) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 px-6 py-10 text-sm text-placeholder">
        <span>FAILED TO LOAD BILLING DETAILS</span>
        <button type="button" className="text-primary text-sm underline" onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <Loader isLoading={isLoading && !invoice}>
      <div className="flex flex-col gap-6 w-full px-6 py-6 text-sm ">
        {emailLogs.length ? (
          <div className="flex flex-col gap-3 w-full">
            <span className="text-primary font-semibold">Emails Sent</span>
            <KeyValueListDisplay
              data={emailLogs.map((log) => ({
                label: log.sentAt
                  ? convertISODateToMMDDYYYY(log.sentAt) + ' ' + convertISOToTime(log.sentAt)
                  : '-',
                value: EmailLogStatusLabel[log.status],
                valueClassName: log.status === EmailStatus.SENT ? 'text-green-700' : 'text-error',
              }))}
            />
          </div>
        ) : null}
        {invoice ? (
          <div
            role="button"
            tabIndex={0}
            className="flex flex-row gap-2 justify-between p-4 rounded-lg bg-[#F5F5F5] cursor-pointer"
            onClick={goToInvoice}
            onKeyDown={(e) => {
              if (e.target !== e.currentTarget) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                goToInvoice();
              }
            }}
          >
            <FileText className="w-6 h-6 text-primary" />
            <div className="flex flex-col gap-2">
              <span className="font-medium text-sm text-primary">{invoice.invoiceNumber}</span>
              <span className="text-xs text-placeholder">{`Updated: ${convertISODateToMMDDYYYY(invoice.modifiedAt)} ${convertISOToTime(invoice.modifiedAt)} by ${invoice.modifiedBy.name}`}</span>
              <div className="font-medium">
                <span className="text-placeholder">Amount: </span>
                <BillingAmount value={totalAmount} className="text-primary" />{' '}
              </div>
            </div>
            <div className="flex flex-col justify-between gap-2">
              <InvoiceStatusBadge key={status} status={invoiceStatusBadgeDetails[status ?? 'DRAFT']} />
              <div className="flex items-end justify-end">
                <Download
                  className="h-5 w-5 text-primary mr-4 cursor-pointer"
                  onClick={async (e) => {
                    e.stopPropagation();
                    await handleDownload(invoice.invoiceId, invoice.invoiceNumber);
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full justify-center flex font-medium text-sm">No Billing Data</div>
        )}
      </div>
    </Loader>
  );
};

export default BillingDdiContent;
